"use client";

import Image from "next/image";
import { ArrowRight } from "lucide-react";

/**
 * Reusable App Install CTA Banner
 * Centered banner with background image, title, description, and button with app icon
 * @param {Object} props
 * @param {string} props.title - Banner title
 * @param {string} props.description - Banner description
 * @param {string} props.ctaText - CTA button text
 * @param {string} props.ctaLink - CTA button link
 * @param {string} props.iconSrc - Path to app icon (round icon in button)
 * @param {string} props.backgroundImage - Path to background image
 */
export const AppInstallCTA = ({
  title = "Don't wait to try us",
  description = "We are here to assist you with the setup and onboarding process.",
  ctaText = "Install app",
  ctaLink = "#",
  iconSrc,
  backgroundImage,
}) => {
  return (
    <section className="py-16 relative z-10 overflow-hidden bg-[#1e1e1e]">
      <div className="mx-auto px-6 md:px-16 lg:px-32 max-w-[1440px]">
        <div className="relative rounded-2xl overflow-hidden max-w-[1184px] mx-auto">
          {/* Background Image */}
          {backgroundImage && (
            <Image
              src={backgroundImage}
              alt=""
              fill
              sizes="100vw"
              className="object-cover pointer-events-none"
            />
          )}

          {/* Content */}
          <div className="relative z-10 flex items-center justify-center p-8">
            <div className="flex flex-col gap-6 items-center justify-center max-w-[1140px] w-full">
              <div className="flex flex-col gap-3 items-center justify-center w-full">
                <h3
                  className="text-3xl md:text-[40px] font-normal text-[#1e1e1e] text-center tracking-[-0.4px] w-full"
                  style={{ lineHeight: "56px" }}
                >
                  {title}
                </h3>
                <p
                  className="text-lg md:text-xl font-normal text-[#3e3e3e] text-center tracking-[-0.04px] w-full"
                  style={{ lineHeight: "28px" }}
                >
                  {description}
                </p>
              </div>

              {/* Button with app icon */}
              <a
                href={ctaLink}
                className="inline-flex items-center gap-0 bg-[rgba(219,218,205,0.5)] border border-[#f8f7f4] rounded-[10px] overflow-hidden pl-3 pr-3.5 py-3 hover:bg-[rgba(219,218,205,0.7)] transition-colors"
              >
                {iconSrc && (
                  <div className="relative w-7 h-7 rounded-full overflow-hidden shrink-0 bg-[#1e1e1e]">
                    <Image
                      src={iconSrc}
                      alt=""
                      fill
                      sizes="28px"
                      className="object-contain p-1"
                    />
                  </div>
                )}
                <span className="text-base font-medium text-[#3e3e3e] tracking-[-0.144px] whitespace-nowrap pl-2.5 pr-2" style={{ lineHeight: "20px" }}>
                  {ctaText}
                </span>
                <ArrowRight className="w-5 h-5 text-[#3e3e3e]" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
